import { useFormikContext } from 'formik';
import moment from 'moment';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import EmptyStateCard from 'packages/kakapo-ui-kit/components/cards/EmptyStateCard';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import PersonAvatar from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';

const Table = styled.div`
	display: flex;
	flex-grow: 1;
	flex-shrink: 1;
	flex-direction: column;
	height: 100%;
`;

const TableContents = styled.div`
	position: relative;
	flex-grow: 1;
	flex-shrink: 1;
`;

const TableRow = styled(Flex)<{ header?: boolean; clickable?: boolean }>`
	flex-direction: row;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	align-items: stretch;
	white-space: nowrap;

	${(props) =>
		props.clickable &&
		`
		cursor: pointer;

		&:hover {
			background: ${props.theme.colors.lightBlueLight};
		}
	`}
`;

const Sticky = styled(Box)`
	display: table;
	position: sticky;
	top: 0;
	background: white;
	z-index: 1;
`;

const TableCell = styled.div<{
	small?: boolean;
	bold?: boolean;
	sortable?: boolean;
	width?: number;
}>`
	padding: 10px;
	color: ${(props) => props.theme.colors.slateBlue};
	width: ${(props) => props.width ?? (props.small ? 50 : 150)}px;
	flex-shrink: 0;
	flex-grow: 0;
	overflow: hidden;
	white-space: nowrap;
	${(props) =>
		props.small ? null : `border-right: 1px solid ${props.theme.colors.lightBlueLight}`};
	display: flex;
	align-items: center;
	flex-flow: row;
	${(props) => props.sortable && `cursor: pointer; user-select: none;`}
	${(props) => props.bold && `font-weight: bold;`}
`;

const CheckboxClickArea = styled.label`
	height: 38px;
	width: 38px;
	display: flex;
	justify-content: center;
	align-items: center;
	margin: -10px 0 -10px -10px;
`;

function useV2() {
	const history = useHistory();
	return history.location.pathname.startsWith('/v2/');
}

function Result(props: {
	index: number;
	style: any;
	data: {
		linked?: boolean;
		results: any[];
		selection: graphql.User[];
		onChange: (selected: graphql.User[]) => void;
		columns: ('firstName' | 'lastName' | 'username' | 'email' | 'lastLoginAt')[];
	};
}) {
	const history = useHistory();
	const workspace = useWorkspace();
	const v2 = useV2();

	if (props.index === 0) {
		return null;
	}

	const result = props.data.results[props.index - 1];

	const lastLoginAt = result.lastLoginAt
		? moment(result.lastLoginAt).format('DD-MM-YYYY HH:mm')
		: '-';

	function toggle() {
		const selected = props.data.selection.filter(({ id }) => id !== result.id);

		if (!props.data.selection.find(({ id }) => id === result.id)) {
			selected.push(result);
		}

		props.data.onChange(selected);
	}

	const url = v2
		? `/v2/settings/employees/${result.id}`
		: `/settings/employees/${result.id}/informations`;

	return (
		<TableRow
			key={result.id}
			clickable
			style={{ ...props.style, width: 'auto' }}
			onClick={props.data.linked !== false ? () => history.push(url) : toggle}
		>
			<TableCell small>
				<CheckboxClickArea onClick={(event) => event.stopPropagation()}>
					<input
						type="checkbox"
						checked={props.data.selection.some(({ id }) => id === result.id)}
						onChange={toggle}
						onClick={(event) => event.stopPropagation()}
					/>
				</CheckboxClickArea>
			</TableCell>
			<TableCell small>
				<PersonAvatar
					size={28}
					person={{
						...result,
						avatar: result.avatarID
							? {
									source: {
										url: `https://assets.ontreams.com/workspaces/${workspace.id}/assets/${result.avatarID}/source`,
									},
							  }
							: undefined,
					}}
				/>
			</TableCell>
			{props.data.columns.includes('firstName') && <TableCell>{result.firstName}</TableCell>}
			{props.data.columns.includes('lastName') && <TableCell>{result.lastName}</TableCell>}
			{props.data.columns.includes('username') && <TableCell>{result.username}</TableCell>}
			{props.data.columns.includes('email') && <TableCell width={250}>{result.email}</TableCell>}
			{props.data.columns.includes('lastLoginAt') && <TableCell>{lastLoginAt}</TableCell>}
		</TableRow>
	);
}

export default function EmployeesList(props: {
	linked?: boolean;
	selection: graphql.User[];
	onChange: (users: graphql.User[]) => void;
	columns?: ('firstName' | 'lastName' | 'username' | 'email' | 'lastLoginAt')[];
}) {
	const formik = useFormikContext<{
		status: graphql.UserStatus[];
		query: string;
		groupIDs: string[];
		cycleTemplateIDs: string[];
		jobTitleIDs: string[];
		isExternal: string;
	}>();
	const { t } = useTranslation();

	const filters: string[] = [];
	const roles = useViewerRoles();

	if (formik.values.groupIDs.length > 0) {
		filters.push('(' + formik.values.groupIDs.map((id) => [`groupID:${id}`]).join(' OR ') + ')');
	}

	if (formik.values.cycleTemplateIDs.length > 0) {
		filters.push('(' + formik.values.cycleTemplateIDs.join(' OR ') + ')');
	}

	if (formik.values.jobTitleIDs.length > 0) {
		filters.push('(' + formik.values.jobTitleIDs.join(' OR ') + ')');
	}

	if (formik.values.isExternal) {
		filters.push(formik.values.isExternal);
	}

	const query = graphql.useManagerInboxQuery({
		fetchPolicy: 'network-only',
		variables: { recurse: true },
		skip: roles.isWorkspaceSuperuser,
	});

	const managees = new Set(query.data?.viewer.managees.map(({ id }) => id) ?? []);

	const { loading, results } = useSearch(formik.values.query, {
		status: formik.values.status,
		filters: filters.join(' AND '),
	});

	const [sortBy, setSortBy] = useState('firstName');
	const [sortDirection, setSortDirection] = useState(1);

	const sortedResults = results
		.filter((result) => {
			if (result.email === 'support@treams.nl') {
				return;
			}

			if (roles.isWorkspaceSuperuser || managees.has(result.id)) {
				return result;
			}
		})
		.sort((a, b) => {
			if (sortBy === 'lastLoginAt') {
				return sortDirection * Math.sign((a.lastLoginAt ?? 0) - (b.lastLoginAt ?? 0));
			}

			if (sortBy === 'firstName') {
				const aName = a.firstName + ' ' + a.lastName;
				const bName = b.firstName + ' ' + b.lastName;
				return sortDirection * aName.localeCompare(bName);
			}

			return sortDirection * a[sortBy].localeCompare(b[sortBy]);
		});

	const sort = (attribute: string) => {
		if (sortBy === attribute) {
			setSortDirection(-sortDirection);
		} else {
			setSortBy(attribute);
			setSortDirection(1);
		}
	};

	const columns = props.columns ?? ['firstName', 'lastName', 'username', 'email', 'lastLoginAt'];

	return (
		<>
			{sortedResults.length > 0 ? (
				<Table>
					<TableContents>
						<AutoSizer>
							{({ height, width }) => (
								<FixedSizeList
									width={width}
									height={height}
									itemCount={sortedResults.length + 1}
									itemSize={40}
									itemData={{
										columns,
										results: sortedResults,
										selection: props.selection,
										onChange: props.onChange,
										linked: props.linked,
									}}
									innerElementType={({ children }: PropsWithChildren<{}>) => {
										return (
											<Box style={{ height: sortedResults.length * 40 + 40 }}>
												<Sticky>
													<TableRow header>
														<TableCell small>
															<CheckboxClickArea>
																<input
																	type="checkbox"
																	checked={props.selection.length === sortedResults.length}
																	onChange={(event) => {
																		if (event.currentTarget.checked) {
																			props.onChange(sortedResults);
																		} else {
																			props.onChange([]);
																		}
																	}}
																/>
															</CheckboxClickArea>
														</TableCell>
														<TableCell small />
														{columns.includes('firstName') && (
															<TableCell
																bold={sortBy === 'firstName'}
																sortable
																onClick={() => sort('firstName')}
															>
																{t('employees.table.header.firstName')}
															</TableCell>
														)}
														{columns.includes('lastName') && (
															<TableCell
																bold={sortBy === 'lastName'}
																sortable
																onClick={() => sort('lastName')}
															>
																{t('employees.table.header.lastName')}
															</TableCell>
														)}
														{columns.includes('username') && (
															<TableCell
																bold={sortBy === 'username'}
																sortable
																onClick={() => sort('username')}
															>
																{t('employees.table.header.username')}
															</TableCell>
														)}
														{columns.includes('email') && (
															<TableCell
																bold={sortBy === 'email'}
																sortable
																onClick={() => sort('email')}
																width={250}
															>
																{t('employees.table.header.email')}
															</TableCell>
														)}
														{columns.includes('lastLoginAt') && (
															<TableCell
																bold={sortBy === 'lastLoginAt'}
																sortable
																onClick={() => sort('lastLoginAt')}
															>
																{t('employees.table.header.lastLoginAt')}
															</TableCell>
														)}
													</TableRow>
												</Sticky>
												{children}
											</Box>
										);
									}}
								>
									{Result}
								</FixedSizeList>
							)}
						</AutoSizer>
					</TableContents>
				</Table>
			) : loading || query.loading ? (
				<Spinner isLoading size="18px" />
			) : (
				sortedResults.length === 0 && (
					<Box padding={20}>
						<EmptyStateCard title={t('employees.search.empty')} />
					</Box>
				)
			)}
		</>
	);
}
